import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { Upload, TrendingUp, DollarSign, FileText, CheckCircle } from "lucide-react";
import { z } from "zod";
import { useQueryClient } from "@tanstack/react-query";

interface AddInvestmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const investmentSchema = z.object({
  name: z.string().trim().min(1, "Investment name is required").max(200),
  assetClass: z.string().min(1, "Asset class is required"),
  symbol: z.string().trim().max(50).optional(),
  amount: z.number().positive("Amount must be positive"),
  quantity: z.number().positive("Quantity must be positive"),
  currency: z.string().length(3, "Currency must be 3 letters"),
  accountNumber: z.string().trim().min(1, "Account number is required").max(50),
  custodian: z.string().trim().min(1, "Custodian is required").max(100),
  asOfDate: z.string().min(1, "Date is required"),
});

type InvestmentFormData = z.infer<typeof investmentSchema>;

export function AddInvestmentDialog({ open, onOpenChange }: AddInvestmentDialogProps) {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { orgId, familyId } = useTenant();
  const queryClient = useQueryClient();

  // Form state
  const [formData, setFormData] = useState<Partial<InvestmentFormData>>({
    currency: "USD",
    asOfDate: new Date().toISOString().split("T")[0],
  });
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const updateFormData = (field: keyof InvestmentFormData, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setUploadedFiles((prev) => [...prev, ...files]);
  };

  const removeFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      // Validate form data
      const validatedData = investmentSchema.parse(formData);

      if (!orgId) {
        throw new Error("Organization context is missing");
      }

      // Get or create default client for the org/family
      let clientQuery = supabase
        .from("clients")
        .select("id")
        .eq("org_id", orgId);

      if (familyId) {
        clientQuery = clientQuery.eq("family_id", familyId);
      }

      const { data: existingClient } = await clientQuery.limit(1).maybeSingle();

      let clientId = existingClient?.id;

      if (!clientId) {
        // Create default client
        const { data: newClient, error: clientError } = await supabase
          .from("clients")
          .insert({
            org_id: orgId,
            family_id: familyId,
            name: familyId ? "Family Client" : "Default Client",
            base_ccy: validatedData.currency,
            tz: "UTC",
          })
          .select("id")
          .single();

        if (clientError) throw clientError;
        clientId = newClient.id;
      }

      // 1. Create or get account
      const { data: existingAccount } = await supabase
        .from("accounts")
        .select("id, entity_id")
        .eq("number", validatedData.accountNumber)
        .eq("custodian", validatedData.custodian)
        .eq("org_id", orgId)
        .maybeSingle();

      let accountId = existingAccount?.id;
      let entityId = existingAccount?.entity_id;

      if (!accountId) {
        // Get or create default entity
        const { data: defaultEntity } = await supabase
          .from("entities")
          .select("id")
          .eq("client_id", clientId!)
          .eq("org_id", orgId)
          .limit(1)
          .maybeSingle();

        if (!defaultEntity) {
          // Create default entity
          const { data: newEntity, error: entityError } = await supabase
            .from("entities")
            .insert({
              client_id: clientId!,
              org_id: orgId,
              family_id: familyId,
              name: "Default Entity",
              type: "family" as const,
            })
            .select("id")
            .single();

          if (entityError) throw entityError;
          entityId = newEntity.id;
        } else {
          entityId = defaultEntity.id;
        }

        // Create new account
        const { data: newAccount, error: accountError } = await supabase
          .from("accounts")
          .insert({
            org_id: orgId,
            family_id: familyId,
            entity_id: entityId,
            number: validatedData.accountNumber,
            custodian: validatedData.custodian,
            ccy: validatedData.currency,
            type: "brokerage",
          })
          .select("id")
          .single();

        if (accountError) throw accountError;
        accountId = newAccount.id;
      }

      // 2. Create holding
      const { error: holdingError } = await supabase.from("holdings").insert({
        org_id: orgId,
        family_id: familyId,
        account_id: accountId,
        name: validatedData.name,
        symbol: validatedData.symbol || validatedData.name.substring(0, 10).toUpperCase(),
        qty: validatedData.quantity,
        cost: validatedData.amount,
        mv: validatedData.amount, // Initial market value = cost
        ccy: validatedData.currency,
        as_of: validatedData.asOfDate,
      });

      if (holdingError) throw holdingError;

      // 3. Upload documents if any
      if (uploadedFiles.length > 0) {
        for (const file of uploadedFiles) {
          const fileExt = file.name.split(".").pop();
          const fileName = `${crypto.randomUUID()}.${fileExt}`;
          const filePath = `${orgId}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from("documents")
            .upload(filePath, file);

          if (uploadError) {
            console.error("File upload error:", uploadError);
            continue;
          }

          // Create document record
          await supabase.from("documents").insert({
            org_id: orgId,
            family_id: familyId,
            client_id: clientId!,
            type: "confirmation",
            storage_url: filePath,
            sha256: "", // Would calculate in production
            source: "manual_upload",
            as_of: validatedData.asOfDate,
          });
        }
      }

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["holdings"] });
      queryClient.invalidateQueries({ queryKey: ["position-metrics"] });

      toast({
        title: "Investment Added",
        description: `${validatedData.name} has been successfully added to your portfolio.`,
      });

      // Reset and close
      handleClose();
    } catch (error) {
      console.error("Error adding investment:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add investment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setFormData({
      currency: "USD",
      asOfDate: new Date().toISOString().split("T")[0],
    });
    setUploadedFiles([]);
    onOpenChange(false);
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.name && formData.assetClass;
      case 2:
        return formData.amount && formData.quantity && formData.currency;
      case 3:
        return formData.accountNumber && formData.custodian;
      case 4:
        return true; // Documents are optional
      default:
        return false;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Add New Investment
          </DialogTitle>
          <DialogDescription>
            Step {step} of 4: {step === 1 && "Investment Details"}
            {step === 2 && "Financial Information"}
            {step === 3 && "Account & Custodian"}
            {step === 4 && "Documents (Optional)"}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {/* Step 1: Investment Details */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Investment Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Apple Inc., S&P 500 ETF"
                  value={formData.name || ""}
                  onChange={(e) => updateFormData("name", e.target.value)}
                  maxLength={200}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="asset-class">Asset Class *</Label>
                <Select
                  value={formData.assetClass}
                  onValueChange={(value) => updateFormData("assetClass", value)}
                >
                  <SelectTrigger id="asset-class">
                    <SelectValue placeholder="Select asset class" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="equity">Public Equity</SelectItem>
                    <SelectItem value="fixed_income">Fixed Income</SelectItem>
                    <SelectItem value="etf">ETF</SelectItem>
                    <SelectItem value="mutual_fund">Mutual Fund</SelectItem>
                    <SelectItem value="alternative">Alternative</SelectItem>
                    <SelectItem value="real_estate">Real Estate</SelectItem>
                    <SelectItem value="cash">Cash & Equivalents</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="symbol">Ticker/Symbol (Optional)</Label>
                <Input
                  id="symbol"
                  placeholder="e.g., AAPL, SPY"
                  value={formData.symbol || ""}
                  onChange={(e) => updateFormData("symbol", e.target.value.toUpperCase())}
                  maxLength={50}
                />
                <p className="text-xs text-muted-foreground">
                  For publicly traded securities
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="as-of-date">Investment Date *</Label>
                <Input
                  id="as-of-date"
                  type="date"
                  value={formData.asOfDate || ""}
                  onChange={(e) => updateFormData("asOfDate", e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Step 2: Financial Information */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="amount">Investment Amount *</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      className="pl-8"
                      value={formData.amount || ""}
                      onChange={(e) => updateFormData("amount", parseFloat(e.target.value))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="currency">Currency *</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) => updateFormData("currency", value)}
                  >
                    <SelectTrigger id="currency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                      <SelectItem value="JPY">JPY</SelectItem>
                      <SelectItem value="CHF">CHF</SelectItem>
                      <SelectItem value="CAD">CAD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="quantity">Quantity/Shares *</Label>
                <Input
                  id="quantity"
                  type="number"
                  step="0.000001"
                  placeholder="0"
                  value={formData.quantity || ""}
                  onChange={(e) => updateFormData("quantity", parseFloat(e.target.value))}
                />
                <p className="text-xs text-muted-foreground">
                  Number of shares or units purchased
                </p>
              </div>

              {formData.amount && formData.quantity && (
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm font-medium">Price per unit</p>
                  <p className="text-2xl font-bold">
                    {formData.currency} {(formData.amount / formData.quantity).toFixed(2)}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Account & Custodian */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="account-number">Account Number *</Label>
                <Input
                  id="account-number"
                  placeholder="e.g., 1234567890"
                  value={formData.accountNumber || ""}
                  onChange={(e) => updateFormData("accountNumber", e.target.value)}
                  maxLength={50}
                />
                <p className="text-xs text-muted-foreground">
                  The account where this investment is held
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="custodian">Custodian/Broker *</Label>
                <Input
                  id="custodian"
                  placeholder="e.g., Fidelity, Charles Schwab, Interactive Brokers"
                  value={formData.custodian || ""}
                  onChange={(e) => updateFormData("custodian", e.target.value)}
                  maxLength={100}
                />
              </div>
            </div>
          )}

          {/* Step 4: Documents */}
          {step === 4 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Upload Supporting Documents (Optional)</Label>
                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground mb-2">
                    Trade confirmations, statements, or other documents
                  </p>
                  <Input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="max-w-xs mx-auto"
                    accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                  />
                </div>
              </div>

              {uploadedFiles.length > 0 && (
                <div className="space-y-2">
                  <Label>Uploaded Files ({uploadedFiles.length})</Label>
                  {uploadedFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{file.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(index)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-start gap-2 p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <CheckCircle className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Ready to Submit</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Review your information and click "Add Investment" to complete.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <Separator />

        <DialogFooter>
          <div className="flex items-center justify-between w-full">
            <div className="flex gap-2">
              {step > 1 && (
                <Button variant="outline" onClick={() => setStep(step - 1)}>
                  Back
                </Button>
              )}
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
            </div>
            <div>
              {step < 4 ? (
                <Button onClick={() => setStep(step + 1)} disabled={!canProceed()}>
                  Next
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={isSubmitting || !canProceed()}>
                  {isSubmitting ? "Adding..." : "Add Investment"}
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
